import * as React from "react";
import {cx} from "emotion";

import "./expandable-streets-table.scss";

import {isApiFalse}             from "@Common/logic/is-api-false";
import {isApiTrue}              from "@Common/logic/is-api-true";
import {cs}                     from "@Common/react/chain-services";
import {scc}                    from "@Common/react/state-cascade-change";
import {stateChangeNestedValue} from "@Common/react/state-change-nested-value";
import {UseState}               from "@Common/react/use-state";
import {plural}                 from "@Common/ui-components/language/plural";
import {ErrorHighlighter}       from "@Common/ui-components/light/error-highlighter";
import {sum}                    from "@Common/utils/collections";
import {chain}                  from "@Common/utils/fs";
import {equalDeep, values}      from "@Common/utils/objects";

import {PageList}             from "../../../../../../../../common/design2/pagination-ui/page-list/page-list";
import {SearchInput}          from "../../../../../../../../common/design2/search-input/search-input";
import {DataTableD1}          from "../../../../../../../../common/design2/data-table/data-table-d1";
import {Checkbox}             from "../../../../../../../../common/design2/checkbox/checkbox";
import {StreetAddressesTable} from "./street-addresses-table/street-addresses-table";

import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronUp, faMinus, faPlus}     from "@fortawesome/free-solid-svg-icons";


export const ExpandableStreetsTable = ({streets, addresses, next: rootNext}) => cs(
    ["eh", (_, next) => ErrorHighlighter({next})],
    ["filter", (_, next) => UseState({next})],
    ["expansions", (_, next) => UseState({initValue: {}, next})],
    ["match", ({filter}, next) => next(!filter.value? (() => true) : (() => {
        const lowerCase = filter.value.toLowerCase();
        return (street) => (
            street.address.toLowerCase().includes(lowerCase) ||
            street.zip_code.startsWith(lowerCase)
        );
    })())],
    ["records", ({}, next) => next(streets.value?.streets && Object["values"](streets.value.streets))],
    ["filteredRecords", ({records, match}, next) => next(chain(
        records,
        (_) => _?.filter((r) => match(getStreet(r))),
    ))],

    ({eh, filter, records, expansions, filteredRecords}) => rootNext({
        getExcludedStreets: () => Object.keys(streets.value.streets).filter((street) => isApiFalse(streets.value.streets[street].addresses[street].display_address)),

        containsAddresses: (
            streets.value?.streets &&
            sum(Object["values"](streets.value.streets), (r) => {
                const street = getStreet(r);
                return !isApiTrue(street.display_address) ? 0 : (
                    +street.count - sum(Object["values"](getExcludedCounts(addresses, street.address)))
                );
            })
        ),

        highlightErrors: () => {
            eh.start();
        },

        getStreetRecord: (street) => streets.value.streets[street],
        getStreetRecords: () => values(streets.value.streets),
        getStreetNames: () => Object.keys(streets.value.streets),
        renderSearch: () => cs(
            () => <div className="controls">
                {SearchInput({
                    state: filter,
                    placeholder: "Search Street",
                    text: filter.value && <>Found <b>{filteredRecords.length}</b> {plural("street", filteredRecords.length)}</>,
                    xdQaName: "Build Radius List Search Street"
                })}
            </div>
        ),
        render: () => cs(
            () => (
                <div className={cx("expandable-streets-table-1i2", eh.css)}>
                    {DataTableD1({
                        className: "table-7e5",
                        pageSize: 10,
                        columns: Columns({
                            count: {
                                selected: records.filter((r) => {
                                    const street = getStreet(r);
                                    const excludedCount = sum(Object["values"](getExcludedCounts(addresses, street.address)));
                                    return +street.count !== excludedCount;
                                }).length,
                                total: records.length,
                            },
                            masterCheckbox: (
                                !filteredRecords.find((r) => {
                                    const street = getStreet(r);
                                    const excludedCount = sum(Object["values"](getExcludedCounts(addresses, street.address)));
                                    return +street.count !== excludedCount;
                                }) ? {
                                    checked: false,
                                    onClick: () => scc(
                                        addresses,
                                        "[*][*].display_address",
                                        (_, {0: street, 1: addressIndex}) => {
                                            const address = addresses.value[street][addressIndex];
                                            return !!filteredRecords.find(r => {
                                                const s = getStreet(r);
                                                return s.address === street;
                                            }) && !address.do_not_mail && !address.excluded_target ? "1" : _
                                        }
                                    ),
                                } : filteredRecords.find((r) => {
                                    const street = getStreet(r);
                                    return !!getExcludedCounts(addresses, street.address).selections;
                                }) ? {
                                    checked: "midway",
                                    onClick: () => scc(
                                        addresses,
                                        "[*][*].display_address",
                                        (_, {0: street, 1: addressIndex}) => {
                                            const address = addresses.value[street][addressIndex];
                                            return !!filteredRecords.find(r => {
                                                const s = getStreet(r);
                                                return s.address === street;
                                            }) && !address.do_not_mail && !address.excluded_target ? "1" : _
                                        }
                                    ),
                                } : {
                                    checked: true,
                                    onClick: () => scc(
                                        addresses,
                                        "[*][*].display_address",
                                        (_, {0: street}) => !!filteredRecords.find(r => {
                                            const s = getStreet(r);
                                            return s.address === street;
                                        }) ? "0" : _
                                    ),
                                }
                            ),
                            expansions,
                            toggleExpansion: (toggle) => expansions.change((_) => ({
                                ...filteredRecords?.reduce((acc, r) => ({...acc, [toKey(getStreet(r))]: toggle}), {})
                            })),
                        }),
                        darkRows: "even",
                        rows: filteredRecords.map((r, i) => {
                            const street = getStreet(r);
                            const excludedCounts = getExcludedCounts(addresses, street.address);
                            const checkbox = sum(Object["values"](excludedCounts)) === +street.count ? {
                                checked: false,
                                onClick: () => addresses.onChange({
                                    ...addresses.value,
                                    [street.address]: addresses.value[street.address].map((a) => ({
                                        ...a,
                                        display_address: !a.do_not_mail && !a.excluded_target ? "1" : "0",
                                    })),
                                }),
                            } : !!excludedCounts.selections ? {
                                checked: "midway",
                                onClick: () => addresses.onChange({
                                    ...addresses.value,
                                    [street.address]: addresses.value[street.address].map((a) => ({
                                        ...a,
                                        display_address: !a.do_not_mail && !a.excluded_target ? "1" : "0",
                                    })),
                                }),
                            } : {
                                checked: true,
                                onClick: () => addresses.onChange({
                                    ...addresses.value,
                                    [street.address]: addresses.value[street.address].map((a) => ({
                                        ...a,
                                        display_address: "0",
                                    })),
                                }),
                            };
                            const expanded = expansions.value?.[toKey(street)];
                            return {
                                className: {expanded},
                                key: street.address,
                                columns: [
                                    {
                                        render: () => Checkbox({
                                            ...checkbox,
                                            xdQaName: "Build Radius List "+street.address.split("^_^")[0]+" "+street.zip_code,
                                        }),
                                    },
                                    {
                                        render: () => {
                                            const excludedCounts = getExcludedCounts(addresses, street.address);
                                            return <>
                                                <div className="text" xd-qa-type="label" xd-qa-name={"Build Radius List "+street.address.split("^_^")[0]+" "+street.zip_code}>
                                                    {street.address.split("^_^")[0]}
                                                </div>
                                                <div className="count" xd-qa-type="label" xd-qa-name={"Build Radius List "+street.address.split("^_^")[0]+" "+street.zip_code+" Count"}>
                                                    ({+street.count - sum(Object["values"](excludedCounts))})
                                                </div>
                                                {excludedCounts.dnm > 0 && <div className="text danger">
                                                    <span xd-qa-type="label" xd-qa-name={"Build Radius List "+street.address.split("^_^")[0]+" "+street.zip_code+" Do Not Mail"}>
                                                        {`(${excludedCounts.dnm} Do Not Mail)`}
                                                    </span>
                                                </div>}
                                            </>
                                        },
                                        sortValue: () => street.address,
                                    },
                                    {
                                        render: () => street.zip_code,
                                        sortValue: () => street.zip_code,
                                        domAttrs: {
                                            "xd-qa-type": "label",
                                            "xd-qa-name": "Build Radius List "+street.address.split("^_^")[0]+" "+street.zip_code+" Zip Code",
                                        },
                                    },
                                    {
                                        render: () => <a>
                                                <FontAwesomeIcon
                                                    key={street.address + "Chevron"}
                                                    className={cx("icon", {expanded})}
                                                    icon={faChevronUp}
                                                    xd-qa-type="icon"
                                                    xd-qa-name={"Build Radius List "+street.address.split("^_^")[0]+" "+street.zip_code+" Expansion"}
                                                    onClick={() => expansions.change((_) => ({..._, [toKey(street)]: !_?.[toKey(street)]}))}
                                                />
                                            </a>,
                                    }
                                ],
                                expansion: {
                                    render: ({colCount}) => expanded && (
                                        <tr>
                                            <td colSpan={colCount}>
                                                {StreetAddressesTable({
                                                    addresses: addresses.value?.[street.address],
                                                    setAddressSelected: (address) => setAddressSelected(addresses, street, address),
                                                    darkRow: !!(i % 2),
                                                })}
                                            </td>
                                        </tr>
                                    )
                                }
                            };
                        }),
                        emptyAction: {
                            label: "No street(s) found",
                        },
                        getFooter: ({colCount, pagination}) => ({
                            columns: [
                                {
                                    colSpan: colCount,
                                    render: () => getFooter({streets, addresses, pagination})
                                },
                            ],
                        }),
                    })}
                </div>
            ),
        ),
    }),
);

const setAddressSelected = (addresses, street, address) => stateChangeNestedValue(
    addresses, 
    [
        street.address,
        addresses.value[street.address].findIndex((a) => equalDeep(a, address)),
        "display_address"
    ],
    () => isApiTrue(address.display_address) ? "0" : "1"
);

const getStreet = (r) => values(r.addresses)[0];

const getExcludedCounts = (addresses, street) => {
    const dnmCount = sum(addresses.value?.[street], (a) => isApiTrue(a.do_not_mail) ? 1 : 0);
    return {
        selections: sum(addresses.value?.[street], (a) => isApiFalse(a.display_address) && isApiFalse(a.excluded_target) ? 1 : 0) - dnmCount,
        dnm: dnmCount
    }
}

const getSumDnmCount = (addresses, streets) => {
    return sum(Object["values"](streets), (r) => {
        const street = getStreet(r);
        return getExcludedCounts(addresses, street.address).dnm;
    })
}

const getSumAddresses = (addresses, streets) => {
    return sum(Object["values"](streets), (r) => {
        const street = getStreet(r);
        return !isApiTrue(street.display_address) ? 0 : (
            +street.count - sum(Object["values"](getExcludedCounts(addresses, street.address)))
        );
    })
}

const Columns = ({masterCheckbox, expansions, toggleExpansion, count}) => [
    {
        width: "1%",
        className: "checkbox",
        render: () => Checkbox({
            ...masterCheckbox,
            xdQaName: "Build Radius List Street Master Checkbox"
        }),
    },
    {
        width: "20%",
        label: "STREET",

        subTitle: `(${count.selected} of ${count.total} streets selected)`,

        className: "address",
        sortable: {
            defaultDirection: "asc",
        },
        domAttrs: {
            "xd-qa-type": "label",
            "xd-qa-name": "Build Radius List Street",
        },
    },
    {
        width: "1%",
        label: "ZIP CODE",
        className: "zip",
        sortable: {
            defaultDirection: "asc",
        },
        domAttrs: {
            "xd-qa-type": "label",
            "xd-qa-name": "Build Radius List Street Zip Code",
        },
    },
    {
        width: "1%",
        className: "expansion",
        domAttrs: {
            "xd-qa-type": "label",
            "xd-qa-name": "Build Radius List Street Expansion",
        },
        render: () => Object.values(expansions.value).some(v => v) ? (
            <a>
                <FontAwesomeIcon key={"header faMinus"} className="icon" icon={faMinus} onClick={() => toggleExpansion(false)}/>
            </a>
        ) : (
            <a>
                <FontAwesomeIcon key={"header faPlus"} className="icon" icon={faPlus} onClick={() => toggleExpansion(true)}/>
            </a>
        )
    }
];

const getFooter = ({streets, addresses, pagination}) => (
    <div className="footer-7e9">
        <div className="summary">
            <table>
                <tbody>
                <tr className="total-addresses">
                    <td className="label" xd-qa-type="label" xd-qa-name="Build Radius List Total Addresses">
                        Total Addresses
                    </td>
                    <td className="value" xd-qa-type="label" xd-qa-name="Build Radius List Total Addresses Amount">
                        {getSumAddresses(addresses, streets.value.streets)}
                    </td>
                    {getSumDnmCount(addresses, streets.value.streets) > 0 && <td className="label danger" xd-qa-type="label" xd-qa-name="Total Do Not Mail">
                        {`(Excluding ${getSumDnmCount(addresses, streets.value.streets)} Do Not Mail)`}
                    </td>}
                </tr>
                <tr className="total-streets">
                    <td className="label" xd-qa-type="label" xd-qa-name="Build Radius List Total Streets">
                        Total Streets
                    </td>
                    <td className="value" xd-qa-type="label" xd-qa-name="Build Radius List Total Streets Amount">
                        {sum(Object["values"](streets.value.streets), (r) => {
                            const street = getStreet(r);
                            const excludedCount = sum(Object["values"](getExcludedCounts(addresses, street.address)));
                            return +street.count !== excludedCount ? 1 : 0;
                        })}
                    </td>
                </tr>
                </tbody>
            </table>
        </div>

        {pagination.active && (
            <div className="pagination">
                {PageList({...pagination, xdQaNamePrefix: "Build Radius List Street"})}
            </div>
        )}
    </div>
);

const toKey = (street) => street.address + "" + street.zip_code;